// Foundations
import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ChangeDetectorRef,
  AfterViewChecked,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// rxjs
import { Observable } from 'rxjs';
import { filter, finalize, map, take } from 'rxjs/operators';

// Models
import { ContactDetails } from '../../../models/contact-details.model';

// Components
import { ContactDetailsFormComponent } from 'src/app/components/_share/contact-details-form/contact-details-form.component';
import { ConfirmDialogComponent } from '../../../components/_share/confirm-dialog/confirm-dialog.component';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';
import {
  AddRelationContactGQL,
  ContactPhoneInput,
  DeleteRelationContactGQL,
  GetRelationContactsGQL,
  PhoneTypeEnum,
  UpdateRelationContactGQL,
} from '../../../services/graphql.service';
import { AuthService } from '../../../auth/auth.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { MyProfileService } from 'src/app/services/my-profile.service';

@Component({
  selector: 'app-other-contacts-page',
  templateUrl: './other-contacts-page.component.html',
  styleUrls: ['./other-contacts-page.component.scss'],
})
export class OtherContactsPageComponent implements OnInit, AfterViewChecked {
  @Input() isFirstTime: boolean;
  @Output() navigateToCampLocation = new EventEmitter<number>();

  @ViewChild('newContactForm', { static: false })
  newContactForm: ContactDetailsFormComponent;
  @ViewChild('editContactForm', { static: false })
  editContactForm: ContactDetailsFormComponent;

  emergencyContactIdList: string[] = [];
  contactList$: Observable<ContactDetails[]>;
  contactToEdit: ContactDetails;

  activeScreen: 'list' | 'add' | 'edit' = 'list';

  constructor(
    private _analytics: AnalyticsService,
    public dialog: MatDialog,
    private getRelationContactsQuery: GetRelationContactsGQL,
    private updateRelationContactMutation: UpdateRelationContactGQL,
    private deleteRelationContactMutation: DeleteRelationContactGQL,
    private addRelationContactMutation: AddRelationContactGQL,
    private authService: AuthService,
    private loadingIndicatorService: LoadingIndicatorService,
    private snackBar: MatSnackBar,
    private cdRef: ChangeDetectorRef,
    private myProfileService: MyProfileService
  ) {}

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.fetchData();
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  showAddScreen() {
    this._analytics.logButtonTap('myProfile_addNewOtherContact');
    this.activeScreen = 'add';
  }

  showEditScreen(contact) {
    this._analytics.logButtonTap('myProfile_viewOtherContact');
    this.contactToEdit = contact;
    this.activeScreen = 'edit';
  }

  onAddContact() {
    this._analytics.logButtonTap('myProfile_saveNewOtherContact');
    this.addContact(this.newContactForm.getForm().value);
  }

  onUpdateContact() {
    this._analytics.logButtonTap('myProfile_updateOtherContact');
    this.updateContact(this.editContactForm.getForm().value);
  }

  onDeleteContact() {
    this._analytics.logButtonTap('myProfile_deleteOtherContact');
    if (
      this.emergencyContactIdList.length === 1 &&
      this.contactToEdit.isEmergencyContact
    ) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'general-dialog',
        data: {
          title: 'OOPS!',
          content: `You must have one emergency contact at all times.`,
          okText: 'OK',
          okFn: () => {
            dialogRef.close();
          },
        },
        autoFocus: false,
        disableClose: true,
      });
    } else {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'general-dialog',
        data: {
          title: 'Remove This Contact',
          content: 'Are you sure you want to delete this contact?',
          cancelText: 'No',
          okText: 'Delete',
          okFn: () => {
            dialogRef.close();
            this.deleteContact();
          },
        },
        autoFocus: false,
      });
    }
  }

  onCancel() {
    this._analytics.logButtonTap('myProfile_cancelOtherContact');
    this.contactToEdit = null;
    this.activeScreen = 'list';
  }

  onContinue() {
    this._analytics.logButtonTap('myProfile_saveStepOtherContact');
    this.navigateToCampLocation.emit(2);
  }

  resetFormToDefault() {
    if (this.activeScreen === 'add') {
      this.newContactForm.contactDetailsForm.reset();
    } else if (this.activeScreen === 'edit') {
      this.editContactForm.contactDetailsForm.reset(this.contactToEdit);
    }
  }

  // Private methods
  // ------------------------------------------------------------------------------------

  private fetchData() {
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.contactList$ = this.getRelationContactsQuery
          .fetch({ accountId })
          .pipe(
            map((res) => {
              this.emergencyContactIdList = [];
              return res.data.getRelationContacts.map((contact) => {
                const {
                  id,
                  firstName,
                  lastName,
                  phones,
                  isEmergencyContact,
                  isRelated,
                  relationship,
                } = contact;
                if (isEmergencyContact) {
                  this.emergencyContactIdList.push(id);
                }
                return {
                  id,
                  firstName,
                  lastName,
                  phones,
                  isEmergencyContact,
                  isRelated,
                  relationship,
                };
              });
            })
          );
      });
  }

  private addContact(contact) {
    this.loadingIndicatorService.show();
    const {
      firstName,
      lastName,
      phones,
      isEmergencyContact,
      isRelated,
      relationship,
    } = contact;
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.addRelationContactMutation
          .mutate({
            accountId,
            relationContactInput: {
              firstName,
              lastName,
              phones: this.formatPhonesInput(phones),
              isEmergencyContact,
              isRelated,
              relationship,
            },
          })
          .pipe(finalize(() => this.loadingIndicatorService.hide()))
          .subscribe((res) => {
            if (res.data) {
              this.activeScreen = 'list';
            } else {
              this.snackBar.open('Add contact failed', '', { duration: 2000 });
            }
          });
      });
  }

  private updateContact(contact) {
    this.loadingIndicatorService.show();
    const id = this.contactToEdit.id;
    const {
      firstName,
      lastName,
      phones,
      isEmergencyContact,
      isRelated,
      relationship,
    } = contact;
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.updateRelationContactMutation
          .mutate({
            accountId,
            relationContactId: id,
            relationContactInput: {
              firstName,
              lastName,
              phones: this.formatPhonesInput(phones),
              isEmergencyContact,
              isRelated,
              relationship,
            },
          })
          .pipe(finalize(() => this.loadingIndicatorService.hide()))
          .subscribe((res) => {
            if (res.data) {
              this.activeScreen = 'list';
            } else {
              this.snackBar.open('Update contact failed', '', {
                duration: 2000,
              });
            }
          });
      });
  }

  private deleteContact() {
    this.loadingIndicatorService.show();
    const id = this.contactToEdit.id;
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.deleteRelationContactMutation
          .mutate({
            accountId,
            relationContactId: id,
          })
          .pipe(finalize(() => this.loadingIndicatorService.hide()))
          .subscribe((res) => {
            if (res.data) {
              this.activeScreen = 'list';
            } else {
              this.snackBar.open('Delete contact failed', '', {
                duration: 2000,
              });
            }
          });
      });
  }

  private getPhoneTypeEnumById(id: string): PhoneTypeEnum {
    const phoneTypeName = this.myProfileService.phoneTypeList.find(
      (phoneType) => phoneType.id === id
    ).type;
    // remove spaces to generate enum value
    return PhoneTypeEnum[phoneTypeName.replace(/\s+/g, '')];
  }

  private formatPhonesInput(phones: any[]): ContactPhoneInput[] {
    return phones.map((phone) => {
      return {
        id: phone.id,
        phoneNumber: phone.phoneNumber,
        type: this.getPhoneTypeEnumById(phone.phoneType.id),
      };
    });
  }
}
